import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, Quote, ChevronLeft, ChevronRight, Calendar, Users, MessageSquare } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const LecturesSection = () => {
  const { lecturesData } = useLanguage();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => 
      prev === lecturesData.testimonials.length - 1 ? 0 : prev + 1
    );
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => 
      prev === 0 ? lecturesData.testimonials.length - 1 : prev - 1
    );
  };

  return (
    <section 
      id="lectures" 
      className="py-20 md:py-32 relative overflow-hidden"
      style={{ background: 'var(--bg-section)' }}
    >
      {/* Background Photo - Right Side */}
      <div className="absolute right-0 top-0 bottom-0 w-2/5 hidden xl:block">
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-[#F5F0E6] z-10" />
        <motion.img
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.9 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          src="https://customer-assets.emergentagent.com/job_65dfb9a0-77f0-47a0-a4bd-aac13a3c81b0/artifacts/4muur0kg_image.png"
          alt="Lucimar Bizoni palestrando"
          className="w-full h-full object-cover"
          style={{ objectPosition: '30% 20%' }}
        />
      </div>

      <div className="container relative z-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 xl:text-left xl:max-w-xl"
        >
          <span className="mono-text mb-4 block" style={{ color: 'var(--accent-gold-400)' }}>
            EVENTOS
          </span>
          <h2 className="heading-hero mb-4">{lecturesData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            {lecturesData.subtitle}
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid xl:grid-cols-2 gap-12 xl:gap-16">
          {/* Left Column - Themes & Audiences */}
          <div className="space-y-6">
            {/* Themes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="voice-card"
              style={{ background: 'var(--bg-card)' }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--accent-gold-200)' }}
                >
                  <MessageSquare size={18} style={{ color: 'var(--accent-gold-400)' }} />
                </div>
                <h3 className="heading-3">Temas</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {lecturesData.themes.map((theme, index) => (
                  <span key={index} className="btn-tag">
                    {theme}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Audiences */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="voice-card accent-bronze"
            >
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.6)' }}
                >
                  <Users size={18} style={{ color: 'var(--text-primary)' }} />
                </div>
                <h3 className="heading-3">Público Atendido</h3>
              </div>
              <ul className="space-y-2">
                {lecturesData.audiences.map((audience, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: 'var(--accent-bronze-400)' }}
                    />
                    <span className="body-small">{audience}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <button
                onClick={() => handleNavClick('#contact')}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
              >
                <Calendar size={18} />
                Agende uma Palestra
              </button>
            </motion.div>
          </div>

          {/* Right Column - Testimonials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="xl:max-w-lg"
          >
            <div 
              className="voice-card h-full flex flex-col"
              style={{ background: 'var(--bg-card)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--accent-rose-200)' }}
                >
                  <Quote size={18} style={{ color: 'var(--accent-rose-400)' }} />
                </div>
                <h3 className="heading-3">Depoimentos</h3>
              </div>

              {/* Testimonial Content */}
              <div className="flex-1">
                <motion.div
                  key={activeTestimonial}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <p 
                    className="text-lg mb-6" 
                    style={{ 
                      color: 'var(--text-secondary)', 
                      fontFamily: 'Cormorant Garamond, serif',
                      fontStyle: 'italic',
                      lineHeight: 1.8
                    }}
                  >
                    "{lecturesData.testimonials[activeTestimonial].quote}"
                  </p>
                  <div>
                    <p className="heading-3" style={{ marginBottom: '0.25rem' }}>
                      {lecturesData.testimonials[activeTestimonial].author}
                    </p>
                    <p className="caption">
                      {lecturesData.testimonials[activeTestimonial].role}
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-6 pt-6" style={{ borderTop: '1px solid var(--border-light)' }}>
                <div className="flex gap-2">
                  {lecturesData.testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTestimonial(index)}
                      className={`h-1.5 rounded-full transition-all duration-200 ${
                        index === activeTestimonial ? 'w-8' : 'w-1.5'
                      }`}
                      style={{ 
                        background: index === activeTestimonial 
                          ? 'var(--accent-gold-400)' 
                          : 'var(--border-light)' 
                      }}
                      aria-label={`Depoimento ${index + 1}`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={prevTestimonial}
                    className="btn-nav"
                    aria-label="Anterior"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="btn-nav"
                    aria-label="Próximo"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LecturesSection;
